import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Card, 
  CardContent, 
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';

export interface AgentOutput {
  task: string;
  result: {
    type: 'table' | 'summary' | 'list';
    data: any;
  };
  ad: {
    title: string;
    description: string;
    cta: string;
  };
}

interface AgentOutputRendererProps {
  output: AgentOutput;
}

const AgentOutputRenderer: React.FC<AgentOutputRendererProps> = ({ output }) => {
  const renderAd = () => (
    <Card sx={{ 
      backgroundColor: '#1E293B', 
      border: '1px solid rgba(148, 163, 184, 0.2)',
      borderRadius: 2,
      mt: 3
    }}>
      <CardContent>
        <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
          {output.ad.title}
        </Typography>
        <Typography sx={{ color: '#94A3B8', mb: 2 }}>
          {output.ad.description}
        </Typography>
        <Button 
          variant="contained" 
          sx={{ 
            backgroundColor: '#3B82F6',
            '&:hover': { backgroundColor: '#2563EB' }
          }}
        >
          {output.ad.cta}
        </Button>
      </CardContent>
    </Card>
  );

  const renderContent = () => {
    switch (output.result.type) {
      case 'table':
        return (
          <TableContainer component={Paper} sx={{ backgroundColor: '#1E293B' }}>
            <Table>
              <TableHead>
                <TableRow>
                  {Object.keys(output.result.data[0]).map((header) => (
                    <TableCell key={header} sx={{ color: 'white' }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {output.result.data.map((row: any, index: number) => (
                  <TableRow key={index}>
                    {Object.values(row).map((cell: any, cellIndex: number) => (
                      <TableCell key={cellIndex} sx={{ color: '#94A3B8' }}>
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      
      case 'summary':
        return (
          <Box sx={{ color: '#94A3B8', whiteSpace: 'pre-line' }}>
            {output.result.data}
          </Box>
        );
      
      case 'list':
        return (
          <Box component="ul" sx={{ color: '#94A3B8', pl: 2 }}>
            {output.result.data.map((item: string, index: number) => (
              <Box component="li" key={index} sx={{ mb: 1 }}>
                {item}
              </Box>
            ))}
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <Paper sx={{ p: 3, backgroundColor: '#1E293B' }}>
      <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
        Agent Output
      </Typography>
      {renderContent()}
      <Divider sx={{ my: 3, borderColor: 'rgba(148, 163, 184, 0.2)' }} />
      {renderAd()}
    </Paper>
  );
};

export default AgentOutputRenderer; 